import { useMemo, useState, type ReactNode } from 'react';
import { Box, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import type { DriverCatalog, ResolvedDriver } from '@casa/driver-host';
import { isFormComplete, readForm, summarize, withFixedValues, type CommandInfo, type Form, type FormField, type FormPath, type SourceDescription } from '@casa/driver-sdk';
import { ConditionSchema, NONE, PRIMARY_SOURCE, tileOptionsJsonSchema, type Connection, type Dashboard, type DataSource, type GaugeStyle, type SlotNeeds, type TileStyle } from '@casa/model';
import { firstGradientColor, tileStyleOf } from '@casa/tiles';
import { SourceSummary, formAskOf, useDriverProbe } from '../drivers/driver_explorer.tsx';
import { ColorField, GradientField, allowsNone } from '../forms/color_fields.tsx';
import { ChoiceField, ScreenRow, TextFieldRow } from '../forms/form_rows.tsx';
import { FormView, type FieldDescriber, type FieldRenderProps, type FieldRenderer } from '../forms/form_view.tsx';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { useAsk, Waiting } from '../ui/asking.tsx';
import { ChromeNote } from '../ui/chrome_page.tsx';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { ConnectionsDialog } from './connections_dialog.tsx';
import { IconSearch } from './edit_dialogs.tsx';
import { GaugeStyleRow } from './gauge_style_picker.tsx';
import { TileStyleFields } from './tile_style_fields.tsx';

// The fields of a tile's options that the form draws its own way (dashboard-editor spec §5.7): a
// slot (the data of one of the tile's sources, chosen on a screen: the connection, then the
// driver's form, T-8 to T-11), an icon (the icon search), a colour (with a swatch), a gradient,
// the command an action sends (asked of its source's driver; a status tile's, which fills its
// states from the command's values, status-tile.md ST-8), the secret video URL (replace-only),
// and a table's rows (a cell per column, each a text or a reading). The rest is the form's own.

export interface TileOptionsContext {
    readonly shell: Bridge;
    readonly dashboard: Dashboard;
    readonly catalog: DriverCatalog | undefined;
    /** The tile's sources as they are now. */
    readonly sources: Readonly<Record<string, DataSource>>;
    /** Keeps a source's data; the tile is written when the options change, or by `commit`. */
    readonly setSource: (name: string, source: DataSource) => void;
    readonly commit: () => void;
    /** A name for a new source of the slot at `path`, free in the tile. */
    readonly newSourceName: (path: FormPath) => string;
    readonly onAddConnection: (name: string, connection: Connection) => void;
    /** The slot to open at once (a new tile's wizard), by its path. */
    readonly opensFirst: string | undefined;
    /** The colours the dashboard already uses, offered first. */
    readonly knownColors: readonly string[];
    /** The tile's own style, under its style rules (dashboard-editor spec §5.10). */
    readonly tileStyle: TileStyle | undefined;
    /** The gauge's style, kept in the tile's style but edited with the gauge (issue #24): what it is left out, and a way to set it. */
    readonly gaugeStyle: { readonly fallback: GaugeStyle; readonly set: (style: GaugeStyle | undefined) => void };
}

const keyOf = (path: FormPath) : string => path.join('/');

/** A data source in a line: its connection and what was chosen. */
function describeTileSource(source: DataSource | undefined) : string | undefined {
    if (!source) {
        return undefined;
    }

    const params = Object.entries(source.params).map(([name, value]) => `${name} ${typeof value === 'object' ? '…' : String(value)}`);

    return [source.connection, ...params].join(' · ');
}

/** What a source reads, in a few words for a summary: its connection and the values chosen. */
function shortSource(source: DataSource) : string {
    const values = Object.values(source.params).filter((value) => typeof value === 'string' || typeof value === 'number').map(String);

    return [source.connection, ...values].join(' · ');
}

/** How a tile's own fields read in the summaries of sub-forms and list items: a slot by what it reads. */
export function tileFieldDescriber(context: TileOptionsContext) : FieldDescriber {
    return (field, value) => {
        if (field.schema.slot !== undefined) {
            const source = typeof value === 'string' ? context.sources[value] : undefined;

            return source ? shortSource(source) : 'not chosen';
        }
        if (field.schema.secret === true) {
            return 'set';
        }
        // A gauge's colours are summed up by its colouring alone (issue #24), not each colour bare.
        if (field.schema['x-in'] === 'gauge-colours') {
            return '';
        }
        if (field.schema['x-hint'] === 'colors' || field.schema['x-hint'] === 'color') {
            return Array.isArray(value) ? value.join(', ') : textOf(value);
        }
        if (field.schema['x-hint'] === 'tile-style') {
            return describeStyle(value);
        }

        return undefined;
    };
}

/**
 * The renderer of a tile's own fields, for `FormView`. A gauge's colour fields are left out, as its
 * "Colours" row shows them, except on that row's own screen (`inGaugeColours`).
 */
export function tileFieldRenderer(context: TileOptionsContext, inGaugeColours = false) : FieldRenderer {
    return (props) => {
        const { field } = props;
        const hint = field.schema['x-hint'];
        if (field.schema['x-in'] === 'gauge-colours' && !inGaugeColours) {
            return null;
        }
        if (field.schema.slot !== undefined) {
            return <SlotRow {...props} context={context} />;
        }
        if (field.schema.secret === true) {
            return <SecretRow {...props} />;
        }

        switch (hint) {
            case 'icon':
                return <IconRow {...props} />;
            case 'tile-style':
                return <RuleStyleRow {...props} context={context} />;
            case 'condition':
                return <ConditionRow {...props} />;
            case 'color':
                return (
                    <ColorField
                        label={field.title}
                        description={field.description}
                        value={textOf(props.value)}
                        fallback={textOf(field.defaultValue)}
                        // A gauge's unfilled part: the style's own when left out, or none.
                        none={allowsNone(field.schema) ? NONE : undefined}
                        defaultName={typeof field.schema['x-none'] === 'string' ? field.schema['x-none'] : undefined}
                        known={context.knownColors}
                        onChange={props.onChange}
                    />
                );
            case 'colors':
                return (
                    <GradientField
                        label={field.title}
                        description={field.description}
                        value={typeof props.value === 'string' || Array.isArray(props.value) ? (props.value as string | string[]) : undefined}
                        fallback={textOf(field.defaultValue)}
                        onChange={props.onChange}
                    />
                );
            case 'command':
                return <CommandRows {...props} context={context} />;
            case 'mode-command':
                return <CommandRows {...props} context={context} isMode />;
            case 'command-name':
                return <CommandRows {...props} context={context} isMode fillsStates={false} />;
            case 'command-argument':
                // Drawn by the command's rows, which know what argument the command takes.
                return null;
            case 'table-rows':
                return <TableRows {...props} context={context} />;
            case 'gauge-colours':
                return (
                    <>
                        <GaugeStyleRow label="Style" value={context.tileStyle?.gaugeStyle} fallback={context.gaugeStyle.fallback} onChange={context.gaugeStyle.set} />
                        <GaugeColoursRow {...props} context={context} />
                    </>
                );
            default:
                return undefined;
        }
    };
}

/**
 * A style rule's condition (dashboard-editor spec §5.10 SR-4): refused where it is typed when it does
 * not parse or names something other than the value, saying why; the rule keeps its last good one.
 */
function ConditionRow({ field, value, onChange }: FieldRenderProps) : ReactNode {
    const [problem, setProblem] = useState<string>();

    return (
        <Box>
            <TextFieldRow
                label={field.title}
                detail={field.description}
                value={textOf(value)}
                placeholder="value > 10"
                onChange={(typed) => {
                    const checked = ConditionSchema.safeParse(typed ?? '');
                    setProblem(checked.success ? undefined : (checked.error.issues[0]?.message ?? 'not a condition'));
                    if (checked.success) {
                        onChange(typed);
                    }
                }}
            />
            {problem ? (
                <Text fontSize="sm" color="red.400" px="4" pb="2" role="alert">
                    Not used: {problem}
                </Text>
            ) : null}
        </Box>
    );
}

/** A style rule's style in a line: what it sets (dashboard-editor spec §5.10). */
function describeStyle(value: unknown) : string {
    const set = typeof value === 'object' && value !== null ? Object.entries(value).filter(([, each]) => each !== undefined) : [];

    return set.length === 0 ? 'Nothing yet' : set.map(([name, each]) => `${name} ${String(each)}`).join(', ');
}

/**
 * A style rule's style (SR-7): a row opening the same fields as the tile's "Tile style", with what
 * the tile has without the rule as the defaults.
 */
function RuleStyleRow({ field, value, onChange, context }: FieldRenderProps & { context: TileOptionsContext }) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const style = (typeof value === 'object' && value !== null ? value : {}) as TileStyle;

    return (
        <ScreenRow
            label={field.title}
            value={describeStyle(style)}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="5">
                    {field.description ? (
                        <Text fontSize="sm" color="fg.muted" px="1">
                            {field.description}
                        </Text>
                    ) : null}
                    <TileStyleFields style={style} fallback={tileStyleOf(context.dashboard.tileStyle, context.tileStyle)} known={context.knownColors} hasText hasValue onChange={onChange} />
                </Stack>
            }
        />
    );
}

type Colouring = 'gradient' | 'threshold' | 'single';

const COLOURINGS: readonly Colouring[] = ['gradient', 'threshold', 'single'];
const COLOURING_NAMES: Readonly<Record<Colouring, string>> = { gradient: 'Gradient', threshold: 'By thresholds', single: 'One colour' };
/** The fields each colouring shows on the Colours screen; the unfilled part with every one. */
const COLOURING_FIELDS: Readonly<Record<Colouring, readonly string[]>> = { gradient: ['colors', 'track'], threshold: ['thresholds', 'track'], single: ['color', 'track'] };

/** How a gauge is coloured, as its drawing takes it: its colouring, else by thresholds where there are any, else one colour where one is set. */
export function colouringOf(gauge: Readonly<Record<string, unknown>>) : Colouring {
    if (gauge.coloring === 'gradient' || gauge.coloring === 'threshold' || gauge.coloring === 'single') {
        return gauge.coloring;
    }
    if (Array.isArray(gauge.thresholds) && gauge.thresholds.length > 0) {
        return 'threshold';
    }

    return gauge.color === undefined ? 'gradient' : 'single';
}

/**
 * A gauge switched to `colouring`: what the other colourings had set goes, so that nothing is set
 * that is not used (issue #24). One colour starts from the gradient's first.
 */
export function withColouring(gauge: Readonly<Record<string, unknown>>, colouring: Colouring, defaultGradient: unknown) : Record<string, unknown> {
    const { coloring: _coloring, colors, thresholds, color, ...rest } = gauge;
    switch (colouring) {
        case 'gradient':
            return { ...rest, ...(colors === undefined ? {} : { colors }), coloring: 'gradient' };
        case 'threshold':
            return { ...rest, ...(thresholds === undefined ? {} : { thresholds }), coloring: 'threshold' };
        case 'single': {
            const gradient = typeof colors === 'string' || Array.isArray(colors) ? colors : typeof defaultGradient === 'string' || Array.isArray(defaultGradient) ? defaultGradient : 'red-green';
            return { ...rest, color: color ?? firstGradientColor(gradient as string | string[]) ?? '#ffffff', coloring: 'single' };
        }
    }
}

/** A gauge's colours in a line: its colouring and what it has. */
function describeColours(gauge: Readonly<Record<string, unknown>>, defaultGradient: unknown) : string {
    switch (colouringOf(gauge)) {
        case 'gradient':
        {
            const gradient = gauge.colors ?? defaultGradient;
            return `Gradient · ${Array.isArray(gradient) ? gradient.join(', ') : (textOf(gradient) ?? '')}`;
        }
        case 'threshold': {
            const count = Array.isArray(gauge.thresholds) ? gauge.thresholds.length : 0;
            return count === 1 ? '1 threshold' : `${count} thresholds`;
        }
        case 'single':
            return `One colour · ${textOf(gauge.color) ?? ''}`;
    }
}

/**
 * A gauge's colours in one row (issue #24): a screen with the colouring, then only what it uses (the
 * gradient, the thresholds or the one colour), and the unfilled part.
 */
function GaugeColoursRow({ scope, setScope, form, context }: FieldRenderProps & { context: TileOptionsContext }) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const colouring = colouringOf(scope);
    const defaultGradient = form.fields.find((each) => each.name === 'colors')?.defaultValue;
    const shown: Form = { fields: form.fields.filter((each) => COLOURING_FIELDS[colouring].includes(each.name)) };

    return (
        <ScreenRow
            label="Colours"
            value={describeColours(scope, defaultGradient)}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="5">
                    <SettingsGroup title="Colouring">
                        {COLOURINGS.map((each) => (
                            <SettingsRow key={each} title={COLOURING_NAMES[each]} isCurrent={each === colouring} opens={false} onClick={() => setScope(withColouring(scope, each, defaultGradient))} />
                        ))}
                    </SettingsGroup>
                    <SettingsGroup>
                        <FormView form={shown} value={scope} onChange={setScope} renderField={tileFieldRenderer(context, true)} describeField={tileFieldDescriber(context)} />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

/** A slot: what its source reads, opening the data screen (§5.7, T-8). */
function SlotRow({ field, value, onChange, path, context }: FieldRenderProps & { context: TileOptionsContext }) : ReactNode {
    const needs = field.schema.slot as SlotNeeds;
    const name = typeof value === 'string' ? value : undefined;
    const source = name === undefined ? undefined : context.sources[name];
    const [isOpen, setOpen] = useState(context.opensFirst === keyOf(path));
    const described = describeTileSource(source);

    return (
        <ScreenRow
            label={field.title}
            // Only a required slot left empty is a warning; an optional one says what empty means.
            value={described ?? (field.isRequired ? 'Not chosen' : (needs.whenUnset ?? 'None'))}
            valueColor={described || !field.isRequired ? undefined : '#fbbf24'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <SlotEditor
                    context={context}
                    needs={needs}
                    current={source}
                    canRemove={!field.isRequired && field.defaultValue === undefined && name !== undefined}
                    onRemove={() => {
                        setOpen(false);
                        onChange(undefined);
                    }}
                    onComplete={(complete) => {
                        const target = name ?? context.newSourceName(path);
                        context.setSource(target, complete);
                        if (target === name) {
                            context.commit();
                        } else {
                            onChange(target);
                        }
                    }}
                />
            }
        />
    );
}

const NO_CONFIG: Record<string, unknown> = {};

/** A form with only its required and fixed fields, for a slot that sends commands to a device. */
function requiredOnly(form: Form) : Form {
    const keep = (fields: readonly FormField[]) : FormField[] => fields.filter((field) => field.isRequired || field.kind === 'fixed');

    return {
        fields: keep(form.fields),
        ...(form.alternatives ? { alternatives: form.alternatives.map((alternative) => ({ ...alternative, form: requiredOnly(alternative.form) })) } : {}),
    };
}

/** The driver form's value from a source: its parameters, and its refresh interval, which the file keeps beside them. */
function formValueOf(source: DataSource | undefined) : Record<string, unknown> {
    if (!source) {
        return {};
    }

    return { ...source.params, ...(source.refreshInterval !== undefined ? { refreshInterval: source.refreshInterval } : {}) };
}

interface SlotEditorProps {
    context: TileOptionsContext;
    needs: SlotNeeds;
    current: DataSource | undefined;
    canRemove: boolean;
    onRemove: () => void;
    /** Called with the source each time its form is complete. */
    onComplete: (source: DataSource) => void;
}

/** A slot's data on its screen: the connection (or a new one, C-6), then the driver's form (T-9), then what it gives. */
function SlotEditor({ context, needs, current, canRemove, onRemove, onComplete }: SlotEditorProps) : ReactNode {
    const { shell, dashboard, catalog } = context;
    const names = Object.keys(dashboard.connections);
    const [connection, setConnection] = useState<string | undefined>(current?.connection ?? (names.length === 1 ? names[0] : undefined));
    const [isAdding, setAdding] = useState(false);
    const [value, setValue] = useState<Record<string, unknown>>(() => formValueOf(current));

    const driverName = (name: string) : string => {
        const resolved = catalog?.resolve(dashboard.connections[name]?.driver ?? '');

        return resolved?.ok ? resolved.manifest.name : (dashboard.connections[name]?.driver ?? '');
    };
    // Resolved once per connection: the probe restarts when its driver changes.
    const connectionDriver = connection === undefined ? undefined : dashboard.connections[connection]?.driver;
    const resolved: ResolvedDriver | undefined = useMemo(() => (connectionDriver === undefined ? undefined : catalog?.resolve(connectionDriver)), [catalog, connectionDriver]);
    const driver = resolved?.ok ? resolved : undefined;
    // A slot that sends commands goes to a device: what narrows a reading (an attribute) does not apply.
    const form = useMemo(() => {
        const read = readForm(driver?.manifest.sourceSchema);

        return needs.commands ? requiredOnly(read) : read;
    }, [driver, needs.commands]);
    const probe = useDriverProbe(shell, connection ?? '', connection === undefined ? NO_CONFIG : dashboard.connections[connection]!.config, driver);
    const ask = useMemo(() => (probe ? formAskOf(probe, form) : undefined), [probe, form]);
    const isEmpty = form.fields.length === 0 && (form.alternatives ?? []).length === 0;

    const sourceOf = (chosen: string, formValue: Record<string, unknown>) : DataSource => {
        const { refreshInterval, ...params } = withFixedValues(form, formValue);
        return { connection: chosen, params, ...(typeof refreshInterval === 'number' ? { refreshInterval } : {}) };
    };
    const change = (chosen: string, formValue: Record<string, unknown>) : void => {
        setValue(formValue);
        if (driver && !isEmpty && isFormComplete(form, formValue)) {
            onComplete(sourceOf(chosen, formValue));
        }
    };
    const choose = (name: string) : void => {
        if (name !== connection) {
            setValue({});
        }
        setConnection(name);
    };

    let rows: ReactNode = null;
    let notes: ReactNode = null;
    if (connection !== undefined) {
        if (!resolved) {
            rows = <Waiting text="Loading the drivers…" />;
        } else if (!resolved.ok) {
            notes = <ChromeNote status="warning" text={`This connection's driver does not run: ${resolved.reason}.`} />;
        } else if (isEmpty) {
            notes = <ChromeNote status="info" text="This connection's driver does not describe its data sources, so they cannot be chosen here." />;
        } else if (probe) {
            rows = <FormView key={connection} form={form} value={value} onChange={(next) => change(connection, next)} ask={ask} needs={needs} />;
            notes = isFormComplete(form, value) ? <SourceSummary probe={probe} options={withFixedValues(form, value)} showsOptions={false} /> : null;
        }
    }

    return (
        <Stack gap="6">
            <SettingsGroup>
                <ChoiceField
                    label="Connection"
                    options={names.map((name) => ({ value: name, label: name, detail: driverName(name) }))}
                    value={connection}
                    onChange={(name) => name !== undefined && choose(name)}
                    placeholder={names.length === 0 ? 'None yet' : 'Choose…'}
                    helper="The system the data comes from: one of the dashboard's connections, or a new one."
                    extras={[{ label: 'New connection…', onSelect: () => setAdding(true) }]}
                />
                {rows}
            </SettingsGroup>
            {notes}
            {canRemove ? (
                <SettingsGroup>
                    <SettingsRow icon="trash" title="Do not use this" color="#f87171" opens={false} onClick={onRemove} />
                </SettingsGroup>
            ) : null}
            {isAdding ? (
                <ConnectionsDialog
                    shell={shell}
                    dashboard={dashboard}
                    startsAdding
                    onKeep={(name, added) => {
                        context.onAddConnection(name, added);
                        choose(name);
                    }}
                    onDelete={() => {}}
                    onClose={() => setAdding(false)}
                />
            ) : null}
        </Stack>
    );
}

/** An icon: its name and glyph, opening the icon search; "Default" goes back to the tile's own. */
function IconRow({ field, value, onChange }: FieldRenderProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const name = typeof value === 'string' ? value : undefined;
    const fallback = typeof field.defaultValue === 'string' ? field.defaultValue : undefined;

    return (
        <ScreenRow
            label={field.title}
            value={
                name ? (
                    <Box display="inline-flex" alignItems="center" gap="2">
                        <IconGlyph name={name} size={18} color="#d4d4d8" />
                        <Text truncate>{name}</Text>
                    </Box>
                ) : 'None'
            }
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <IconSearch
                    none={fallback ? `Default (${fallback})` : 'No icon'}
                    onPick={(picked) => {
                        onChange(picked);
                        setOpen(false);
                    }}
                />
            }
        />
    );
}

const textOf = (value: unknown) : string | undefined => (typeof value === 'string' || typeof value === 'number' ? String(value) : undefined);

/** A secret (the video URL): never shown, replaced when typed (as a connection's secrets, C-3). */
function SecretRow({ field, value, onChange }: FieldRenderProps) : ReactNode {
    const isStored = typeof value === 'string' && value !== '';

    return (
        <TextFieldRow
            label={field.title}
            detail={field.description}
            value={undefined}
            type="password"
            placeholder={isStored ? 'Stored; type a new one to replace it' : 'Required'}
            onChange={(typed) => {
                if (typed !== undefined) {
                    onChange(typed);
                }
            }}
        />
    );
}

/** An action's command, from those its source takes, with its argument: asked of the source's driver. */
/**
 * The command a tile sends, from its source's commands: an action's with its argument; a status
 * tile's (`isMode`) without one, or none, and its states filled from the command's values while it
 * has none (status-tile.md ST-8, QS-3); a thermostat's (`fillsStates` off), only its name.
 */
function CommandRows({ field, value, scope, setScope, context, isMode = false, fillsStates = true }: FieldRenderProps & { context: TileOptionsContext; isMode?: boolean; fillsStates?: boolean }) : ReactNode {
    const name = typeof scope.source === 'string' ? scope.source : PRIMARY_SOURCE;
    const source = context.sources[name];
    const connection = source ? context.dashboard.connections[source.connection] : undefined;
    const resolved = useMemo(() => (connection ? context.catalog?.resolve(connection.driver) : undefined), [connection, context.catalog]);
    const probe = useDriverProbe(context.shell, source?.connection ?? '', connection?.config ?? NO_CONFIG, resolved?.ok ? resolved : undefined);
    const options = source?.params ?? {};
    const [asked, retry] = useAsk<SourceDescription>(probe ? JSON.stringify([source?.connection, options]) : undefined, () => (probe ? probe.ask({ kind: 'describe', options }) : Promise.reject(new Error('no source'))));
    const commands = asked?.status === 'done' ? asked.value.commands : [];
    const chosen = typeof value === 'string' ? value : undefined;
    const selected: CommandInfo | undefined = commands.find((command) => command.name === chosen);
    const argument = selected?.argument;

    if (!source) {
        return <ScreenRow label={field.title} value="Choose the device first" isDisabled isOpen={false} onOpen={() => {}} onClose={() => {}} screen={null} />;
    }

    const set = (command: string | undefined, commandArgument: unknown) : void => {
        const { command: _command, argument: _argument, ...rest } = scope;
        setScope({ ...rest, ...(command !== undefined ? { command } : {}), ...(commandArgument !== undefined ? { argument: commandArgument } : {}) });
    };
    const setMode = (command: string | undefined) : void => {
        const { command: _command, ...rest } = scope;
        const values = commands.find((each) => each.name === command)?.argument?.values;
        const hasStates = Array.isArray(scope.states) && scope.states.length > 0;
        const filled = fillsStates && values && !hasStates ? { states: values.map((each) => ({ value: each.value, ...(each.label ? { text: each.label } : {}) })) } : {};
        setScope({ ...rest, ...(command !== undefined ? { command } : {}), ...filled });
    };

    if (isMode) {
        return (
            <ChoiceField
                label={field.title}
                options={commands.map((command) => ({ value: command.name, label: command.label ?? command.name, detail: argumentDetail(command.argument) }))}
                value={chosen}
                isOptional
                onChange={setMode}
                placeholder="Choose…"
                helper={field.description}
                waiting={!asked || asked.status === 'waiting' ? 'Asking the driver…' : undefined}
                failed={asked?.status === 'failed' ? { reason: asked.reason, onRetry: retry } : undefined}
            />
        );
    }

    return (
        <>
            <ChoiceField
                label={field.title}
                options={commands.map((command) => ({
                    value: command.name,
                    label: command.label ?? command.name,
                    detail: argumentDetail(command.argument),
                }))}
                value={chosen}
                onChange={(picked) => set(picked, undefined)}
                placeholder={asked?.status === 'done' && commands.length === 0 ? 'It takes no commands' : 'Choose…'}
                helper={field.description}
                waiting={!asked || asked.status === 'waiting' ? 'Asking the driver…' : undefined}
                failed={asked?.status === 'failed' ? { reason: asked.reason, onRetry: retry } : undefined}
            />
            {selected && argument?.values ? (
                // One of a list (a charger's mode, EVCC spec EV-17): picked, not typed.
                <ChoiceField
                    label="Argument"
                    options={argument.values.map((each) => ({ value: String(each.value), label: each.label ?? String(each.value), ...(each.label ? { detail: String(each.value) } : {}) }))}
                    value={scope.argument === undefined ? undefined : String(scope.argument)}
                    onChange={(picked) => set(selected.name, picked === undefined ? undefined : argument.kind === 'number' ? Number(picked) : picked)}
                    placeholder="Choose…"
                />
            ) : selected && argument ? (
                <TextFieldRow
                    key={`${selected.name}:${textOf(scope.argument) ?? ''}`}
                    label="Argument"
                    type={argument.kind === 'number' ? 'number' : 'text'}
                    placeholder={argument.kind === 'number' ? `A number${argument.unit ? ` in ${argument.unit}` : ''}` : 'A text'}
                    value={textOf(scope.argument)}
                    onChange={(text) => set(selected.name, text === undefined ? undefined : argument.kind === 'number' ? Number(text) : text)}
                />
            ) : null}
        </>
    );
}

/** What a command's argument is, in the list of commands: one of a list, or a number or a text. */
function argumentDetail(argument: CommandInfo['argument']) : string | undefined {
    if (!argument) {
        return undefined;
    }
    if (argument.values) {
        return `takes ${argument.values.map((each) => each.label ?? String(each.value)).join(', ')}`;
    }

    return `takes ${argument.kind === 'number' ? 'a number' : 'a text'}${argument.unit ? ` in ${argument.unit}` : ''}`;
}

/** A table cell as a form: a text, or a reading with its format, transform and colour. */
function cellForm() : Form {
    const table = tileOptionsJsonSchema('table') as { properties: { rows: { items: { items: { anyOf: Record<string, unknown>[] } } } } };
    const reading = table.properties.rows.items.items.anyOf.find((variant) => variant.type === 'object') ?? {};

    return readForm({
        type: 'object',
        oneOf: [
            { title: 'Text', description: 'words that do not change', properties: { text: { type: 'string', title: 'Text' } } },
            { ...reading, title: 'Reading', description: 'a number from one of the tile\'s sources' },
        ],
    });
}

const cellValueOf = (cell: unknown) : Record<string, unknown> => (typeof cell === 'string' ? { text: cell } : typeof cell === 'object' && cell !== null ? (cell as Record<string, unknown>) : { text: '' });

/**
 * The cell as the file writes it: a string for a text, the reading's object otherwise, also while
 * its source is not chosen yet (the dialog then says it is not finished). An empty value is the
 * other way just chosen: a text that becomes a reading, or a reading that becomes a text.
 */
function cellOf(value: Record<string, unknown>, before: unknown) : unknown {
    if ('text' in value) {
        return typeof value.text === 'string' ? value.text : '';
    }
    if (Object.keys(value).length === 0) {
        return typeof before === 'string' ? {} : '';
    }

    return value;
}

/** A table's rows (the one hand-made part, §5.7): each row a screen with a cell per column. */
function TableRows({ field, value, scope, path, context, onChange }: FieldRenderProps & { context: TileOptionsContext }) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const columns = Array.isArray(scope.columns) ? (scope.columns as { label?: string }[]) : [];
    const rows = Array.isArray(value) ? (value as unknown[][]) : [];
    const form = useMemo(cellForm, []);
    const renderField = useMemo(() => tileFieldRenderer(context), [context]);
    const describeField = useMemo(() => tileFieldDescriber(context), [context]);
    const write = (next: unknown[][]) : void => onChange(next);

    return (
        <ScreenRow
            label={field.title}
            value={rows.length === 0 ? 'None' : `${rows.length}`}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <Text fontSize="sm" color="fg.muted" px="1">
                        {field.description}
                    </Text>
                    <SettingsGroup>
                        {rows.map((row, index) => (
                            <TableRow
                                key={index}
                                index={index}
                                row={row}
                                columns={columns}
                                form={form}
                                path={[...path, index]}
                                renderField={renderField}
                                describeField={describeField}
                                isFirst={index === 0}
                                isLast={index === rows.length - 1}
                                onChange={(next) => write(rows.map((other, at) => (at === index ? next : other)))}
                                onMove={(by) => {
                                    const moved = [...rows];
                                    const [taken] = moved.splice(index, 1);
                                    moved.splice(index + by, 0, taken!);
                                    write(moved);
                                }}
                                onRemove={() => write(rows.filter((_, at) => at !== index))}
                            />
                        ))}
                        <SettingsRow icon="plus" title="Add row" color="#60a5fa" opens={false} onClick={() => write([...rows, columns.map(() => '')])} />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

interface TableRowProps {
    index: number;
    row: readonly unknown[];
    columns: readonly { label?: string }[];
    form: Form;
    path: FormPath;
    renderField: FieldRenderer;
    describeField: FieldDescriber;
    isFirst: boolean;
    isLast: boolean;
    onChange: (row: unknown[]) => void;
    onMove: (by: -1 | 1) => void;
    onRemove: () => void;
}

/** One row of a table: a cell per column, then moving and removing it. */
function TableRow({ index, row, columns, form, path, renderField, describeField, isFirst, isLast, onChange, onMove, onRemove }: TableRowProps) : ReactNode {
    const [isOpen, setOpen] = useState(false);
    const summary = row.map((cell) => (typeof cell === 'string' ? cell : summarize(form, cellValueOf(cell), describeField))).filter((part) => part !== '').join(' · ');

    return (
        <ScreenRow
            label={`Row ${index + 1}`}
            value={summary || 'Empty'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <Stack gap="4">
                    <SettingsGroup>
                        {columns.map((column, at) => (
                            <CellRow
                                key={at}
                                label={column.label || `Column ${at + 1}`}
                                cell={row[at]}
                                form={form}
                                path={[...path, at]}
                                renderField={renderField}
                                describeField={describeField}
                                onChange={(cell) => onChange(columns.map((_, other) => (other === at ? cell : (row[other] ?? ''))))}
                            />
                        ))}
                    </SettingsGroup>
                    <SettingsGroup>
                        {isFirst ? null : <SettingsRow icon="arrow-up" title="Move up" opens={false} onClick={() => onMove(-1)} />}
                        {isLast ? null : <SettingsRow icon="arrow-down" title="Move down" opens={false} onClick={() => onMove(1)} />}
                        <SettingsRow
                            icon="trash"
                            title="Remove"
                            color="#f87171"
                            opens={false}
                            onClick={() => {
                                setOpen(false);
                                onRemove();
                            }}
                        />
                    </SettingsGroup>
                </Stack>
            }
        />
    );
}

/** One cell: a text or a reading, on its own screen. */
function CellRow(props: { label: string; cell: unknown; form: Form; path: FormPath; renderField: FieldRenderer; describeField: FieldDescriber; onChange: (cell: unknown) => void }) : ReactNode {
    const { label, cell, form, path, renderField, describeField, onChange } = props;
    const [isOpen, setOpen] = useState(false);
    const value = cellValueOf(cell);

    return (
        <ScreenRow
            label={label}
            value={typeof cell === 'string' ? cell || 'Empty' : summarize(form, value, describeField) || 'A reading'}
            isOpen={isOpen}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            screen={
                <SettingsGroup>
                    <FormView form={form} value={value} onChange={(next) => onChange(cellOf(next, cell))} path={path} renderField={renderField} describeField={describeField} initialWay={typeof cell === 'object' && cell !== null ? 1 : 0} />
                </SettingsGroup>
            }
        />
    );
}
